/**
 * PreMeetingChecklist
 *
 * Five-step pre-flight wizard the participant walks through before
 * connecting to LiveKit. Steps adapt to the meeting type — audio
 * calls skip camera + screen-share + document, etc.
 *
 *   1. Mic check       — live level meter for 3s, "did the bars move?"
 *   2. Cam check       — preview video, device picker
 *   3. Sound check     — short test tone via Web Audio
 *   4. Screen-share    — getDisplayMedia probe (mostly for iOS/Safari)
 *   5. Document        — optional file picker for "present this later"
 *
 * Devices selected here flow back to the caller via onComplete() so
 * the LiveKit connect call can honor them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    AlertTriangle, Check, ChevronRight, FileText, MicOff,
    Monitor, ScreenShare, Sparkles, Video as VideoIcon, VideoOff,
    Volume2, X,
} from 'lucide-react';

export type MeetingType = 'video' | 'conference' | 'audio';

export interface ChecklistResult {
    /** ID of the audio input device the operator chose (or undefined
     *  to let LiveKit pick the default). */
    audioInputDeviceId?: string;
    /** ID of the video input device. Undefined for audio calls. */
    videoInputDeviceId?: string;
    /** Pre-selected document the operator wants to present once in the
     *  room. We stash the File ref here; in-room presentation logic
     *  (PR C) picks it up via the same onComplete callback. */
    presentationFile?: File;
    /** True if the operator skipped one or more checks. The room
     *  surfaces a small banner so they know to verify mid-call. */
    skippedSomeChecks: boolean;
}

interface Props {
    meetingType: MeetingType;
    onComplete: (result: ChecklistResult) => void;
    onCancel: () => void;
}


// Step ordering per meeting type. Audio calls don't need cam or
// screen-share; document is offered everywhere since you might want
// to share a contract on a "video showing" call too.
const STEPS_BY_TYPE: Record<MeetingType, StepKey[]> = {
    audio: ['mic', 'sound', 'doc'],
    video: ['mic', 'cam', 'sound', 'screen', 'doc'],
    conference: ['mic', 'cam', 'sound', 'screen', 'doc'],
};

type StepKey = 'mic' | 'cam' | 'sound' | 'screen' | 'doc';


export function PreMeetingChecklist({ meetingType, onComplete, onCancel }: Props) {
    const steps = STEPS_BY_TYPE[meetingType] || STEPS_BY_TYPE.video;
    const [stepIdx, setStepIdx] = useState(0);
    const currentStep: StepKey | null = steps[stepIdx] ?? null;

    // Cumulative result the wizard hands back at the end.
    const [audioInputDeviceId, setAudioInputDeviceId] = useState<string | undefined>();
    const [videoInputDeviceId, setVideoInputDeviceId] = useState<string | undefined>();
    const [presentationFile, setPresentationFile] = useState<File | undefined>();
    const [stepsSkipped, setStepsSkipped] = useState(0);

    // Step navigation. `skip` and `pass` both move forward; we just
    // bookkeep how many got skipped so the room can show a banner.
    const advance = useCallback((skipped: boolean) => {
        if (skipped) setStepsSkipped((n) => n + 1);
        setStepIdx((i) => i + 1);
    }, []);

    // Finalize when we're past the last step.
    useEffect(() => {
        if (stepIdx >= steps.length) {
            onComplete({
                audioInputDeviceId,
                videoInputDeviceId,
                presentationFile,
                skippedSomeChecks: stepsSkipped > 0,
            });
        }
    }, [stepIdx, steps.length, onComplete, audioInputDeviceId, videoInputDeviceId, presentationFile, stepsSkipped]);

    if (!currentStep) {
        // Rendering nothing while the final-effect fires the onComplete.
        return null;
    }

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#0b0c0f] p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
                {/* Progress header */}
                <header className="border-b border-gray-100 px-5 py-3">
                    <div className="flex items-center justify-between gap-3">
                        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                            <Sparkles size={14} className="text-primary-dark" />
                            Pre-meeting checks
                        </h2>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                            aria-label="Cancel and leave the room"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <ProgressDots total={steps.length} current={stepIdx} />
                </header>

                {/* Step body */}
                <div className="px-5 py-4">
                    {currentStep === 'mic' && (
                        <MicStep
                            onPick={(id) => { setAudioInputDeviceId(id); advance(false); }}
                            onSkip={() => advance(true)}
                        />
                    )}
                    {currentStep === 'cam' && (
                        <CamStep
                            onPick={(id) => { setVideoInputDeviceId(id); advance(false); }}
                            onSkip={() => advance(true)}
                        />
                    )}
                    {currentStep === 'sound' && (
                        <SoundStep onPass={() => advance(false)} onSkip={() => advance(true)} />
                    )}
                    {currentStep === 'screen' && (
                        <ScreenStep onPass={() => advance(false)} onSkip={() => advance(true)} />
                    )}
                    {currentStep === 'doc' && (
                        <DocStep
                            onPick={(f) => { setPresentationFile(f); advance(false); }}
                            onSkip={() => advance(true)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}


// ── Step components ─────────────────────────────────────────────────


function MicStep({ onPick, onSkip }: { onPick: (id?: string) => void; onSkip: () => void }) {
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selected, setSelected] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const rafRef = useRef<number | null>(null);

    // Acquire a mic stream + enumerate devices once on mount.
    useEffect(() => {
        let mounted = true;
        navigator.mediaDevices
            .getUserMedia({ audio: { deviceId: selected || undefined } })
            .then(async (s) => {
                if (!mounted) {
                    s.getTracks().forEach((t) => t.stop());
                    return;
                }
                setStream(s);
                const list = await navigator.mediaDevices.enumerateDevices();
                if (!mounted) return;
                setDevices(list.filter((d) => d.kind === 'audioinput'));
            })
            .catch((err: Error) => {
                if (!mounted) return;
                setError(err.name === 'NotAllowedError'
                    ? 'Microphone permission denied. Allow it in your browser settings, then retry.'
                    : `Could not access microphone: ${err.message}`);
            });
        return () => {
            mounted = false;
        };
    // Intentionally not depending on `selected` — the user picks a
    // device from the dropdown which re-runs this effect via the
    // selection effect below.
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-acquire the stream when the user picks a different device.
    useEffect(() => {
        if (!selected) return;
        stream?.getTracks().forEach((t) => t.stop());
        navigator.mediaDevices
            .getUserMedia({ audio: { deviceId: { exact: selected } } })
            .then(setStream)
            .catch(() => {
                /* fall back to whatever's already up */
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    // Render a live level meter on the canvas while we have a stream.
    useEffect(() => {
        if (!stream || !canvasRef.current) return;
        const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const canvas = canvasRef.current;
        const c2d = canvas.getContext('2d');

        const draw = () => {
            analyser.getByteFrequencyData(buf);
            const avg = buf.reduce((s, v) => s + v, 0) / buf.length;
            const level = Math.min(1, avg / 80); // 80/255 ≈ a calm-speaking ceiling
            if (c2d) {
                c2d.clearRect(0, 0, canvas.width, canvas.height);
                const w = canvas.width * level;
                c2d.fillStyle = level > 0.7 ? '#ef4444' : level > 0.05 ? '#10b981' : '#9ca3af';
                c2d.fillRect(0, 0, w, canvas.height);
            }
            rafRef.current = requestAnimationFrame(draw);
        };
        draw();
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            src.disconnect();
            ctx.close().catch(() => undefined);
        };
    }, [stream]);

    const handleProceed = () => {
        stream?.getTracks().forEach((t) => t.stop());
        onPick(selected || undefined);
    };

    return (
        <StepFrame
            title="Microphone check"
            description="Speak into your mic — the green bar should react to your voice."
            icon={<Volume2 size={18} className="text-primary-dark" />}
        >
            {error ? (
                <ErrorBanner message={error} />
            ) : (
                <>
                    <canvas
                        ref={canvasRef}
                        width={400}
                        height={14}
                        className="w-full rounded-md bg-gray-100"
                    />
                    {devices.length > 1 && (
                        <select
                            value={selected}
                            onChange={(e) => setSelected(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        >
                            <option value="">Default microphone</option>
                            {devices.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label || `Microphone ${d.deviceId.slice(0, 6)}`}
                                </option>
                            ))}
                        </select>
                    )}
                </>
            )}
            <StepActions
                onPrimary={handleProceed}
                primaryLabel="Looks good"
                onSkip={onSkip}
                primaryDisabled={!stream && !error}
            />
        </StepFrame>
    );
}


function CamStep({ onPick, onSkip }: { onPick: (id?: string) => void; onSkip: () => void }) {
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selected, setSelected] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        let mounted = true;
        navigator.mediaDevices
            .getUserMedia({ video: true })
            .then(async (s) => {
                if (!mounted) {
                    s.getTracks().forEach((t) => t.stop());
                    return;
                }
                setStream(s);
                const list = await navigator.mediaDevices.enumerateDevices();
                if (!mounted) return;
                setDevices(list.filter((d) => d.kind === 'videoinput'));
            })
            .catch((err: Error) => {
                if (!mounted) return;
                setError(err.name === 'NotAllowedError'
                    ? 'Camera permission denied. Allow it in your browser settings, then retry.'
                    : `Could not access camera: ${err.message}`);
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!selected) return;
        stream?.getTracks().forEach((t) => t.stop());
        navigator.mediaDevices
            .getUserMedia({ video: { deviceId: { exact: selected } } })
            .then(setStream)
            .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    useEffect(() => {
        if (stream && videoRef.current) {
            videoRef.current.srcObject = stream;
        }
        return () => {
            stream?.getTracks().forEach((t) => t.stop());
        };
    }, [stream]);

    const handleProceed = () => {
        stream?.getTracks().forEach((t) => t.stop());
        onPick(selected || undefined);
    };

    return (
        <StepFrame
            title="Camera check"
            description="You should see yourself in the preview below."
            icon={<VideoIcon size={18} className="text-primary-dark" />}
        >
            {error ? (
                <ErrorBanner message={error} />
            ) : (
                <>
                    <div className="overflow-hidden rounded-lg bg-black">
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className="w-full h-48 object-cover"
                        />
                    </div>
                    {devices.length > 1 && (
                        <select
                            value={selected}
                            onChange={(e) => setSelected(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        >
                            <option value="">Default camera</option>
                            {devices.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label || `Camera ${d.deviceId.slice(0, 6)}`}
                                </option>
                            ))}
                        </select>
                    )}
                </>
            )}
            <StepActions
                onPrimary={handleProceed}
                primaryLabel="I can see myself"
                onSkip={onSkip}
                primaryDisabled={!stream && !error}
            />
        </StepFrame>
    );
}


function SoundStep({ onPass, onSkip }: { onPass: () => void; onSkip: () => void }) {
    const [played, setPlayed] = useState(false);

    const playTone = () => {
        try {
            const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            // 440Hz A4 for ~0.4s with a gentle envelope so it doesn't
            // pop. Loud enough to be obvious, soft enough not to make
            // someone jump.
            osc.frequency.value = 440;
            osc.type = 'sine';
            gain.gain.setValueAtTime(0, ctx.currentTime);
            gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.02);
            gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.42);
            osc.connect(gain).connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.45);
            setTimeout(() => ctx.close().catch(() => undefined), 600);
            setPlayed(true);
        } catch {
            setPlayed(true); // graceful even if Web Audio isn't available
        }
    };

    return (
        <StepFrame
            title="Sound check"
            description="Press Play and confirm the test tone came through the right speakers / headphones."
            icon={<Volume2 size={18} className="text-primary-dark" />}
        >
            <button
                type="button"
                onClick={playTone}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm font-medium text-primary-dark hover:bg-primary/10"
            >
                <Volume2 size={14} /> Play test tone
            </button>
            <p className="mt-2 text-xs text-gray-500">
                Some browsers route output to the system default. To pick a different speaker, change
                your OS audio output before joining.
            </p>
            <StepActions
                onPrimary={onPass}
                primaryLabel={played ? "I heard it" : "I heard it (skip play)"}
                onSkip={onSkip}
            />
        </StepFrame>
    );
}


function ScreenStep({ onPass, onSkip }: { onPass: () => void; onSkip: () => void }) {
    const [error, setError] = useState<string | null>(null);
    const [tested, setTested] = useState(false);

    const testScreen = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
            // We just want to confirm the permission flow worked.
            // Stop the tracks immediately — we'll re-acquire in-room
            // when the host actually shares.
            stream.getTracks().forEach((t) => t.stop());
            setTested(true);
            setError(null);
        } catch (err) {
            const e = err as Error;
            setError(e.name === 'NotAllowedError'
                ? 'Screen-share permission denied. On iOS / iPadOS this needs to be enabled in Safari settings.'
                : `Couldn't test screen share: ${e.message}`);
        }
    };

    return (
        <StepFrame
            title="Screen sharing"
            description="Some browsers (iOS Safari especially) need an explicit permission to share your screen. Click below to verify it works on this device."
            icon={<ScreenShare size={18} className="text-primary-dark" />}
        >
            <button
                type="button"
                onClick={testScreen}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm font-medium text-primary-dark hover:bg-primary/10"
            >
                <Monitor size={14} /> {tested ? 'Test again' : 'Test screen share'}
            </button>
            {error && <ErrorBanner message={error} className="mt-3" />}
            {tested && !error && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800">
                    <Check size={12} /> Screen-share works on this device.
                </div>
            )}
            <StepActions
                onPrimary={onPass}
                primaryLabel={tested ? "Continue" : "I'll skip this for now"}
                onSkip={onSkip}
            />
        </StepFrame>
    );
}


function DocStep({ onPick, onSkip }: { onPick: (file?: File) => void; onSkip: () => void }) {
    const [file, setFile] = useState<File | null>(null);

    return (
        <StepFrame
            title="Pick a document to present"
            description="Optional. Choose a PDF / image / slide deck now and you'll be able to present it directly instead of sharing your full screen during the meeting."
            icon={<FileText size={18} className="text-primary-dark" />}
        >
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-600 hover:border-primary hover:bg-primary/5">
                <FileText size={20} className="text-gray-400" />
                <span className="text-xs">
                    {file ? <strong>{file.name}</strong> : 'Click to choose a file…'}
                </span>
                <input
                    type="file"
                    accept=".pdf,.docx,.doc,.pptx,.ppt,.txt,image/*"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
            </label>
            <StepActions
                onPrimary={() => onPick(file || undefined)}
                primaryLabel={file ? 'Use this document' : "I'll just share my screen"}
                onSkip={onSkip}
            />
        </StepFrame>
    );
}


// ── Building blocks ─────────────────────────────────────────────────


function StepFrame({
    title,
    description,
    icon,
    children,
}: {
    title: string;
    description: string;
    icon: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                {icon}
                <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            </div>
            <p className="text-xs text-gray-500">{description}</p>
            {children}
        </div>
    );
}


function StepActions({
    onPrimary,
    primaryLabel,
    onSkip,
    primaryDisabled,
}: {
    onPrimary: () => void;
    primaryLabel: string;
    onSkip: () => void;
    primaryDisabled?: boolean;
}) {
    return (
        <div className="mt-4 flex items-center justify-between gap-2">
            <button
                type="button"
                onClick={onSkip}
                className="text-xs text-gray-400 hover:text-gray-700 underline underline-offset-2"
            >
                Skip this check
            </button>
            <button
                type="button"
                onClick={onPrimary}
                disabled={primaryDisabled}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
                {primaryLabel} <ChevronRight size={14} />
            </button>
        </div>
    );
}


function ProgressDots({ total, current }: { total: number; current: number }) {
    return (
        <div className="mt-2 flex items-center gap-1">
            {Array.from({ length: total }).map((_, i) => (
                <span
                    key={i}
                    className={[
                        'h-1 rounded-full transition-all',
                        i < current ? 'flex-1 bg-primary' : i === current ? 'flex-1 bg-primary/40' : 'flex-1 bg-gray-200',
                    ].join(' ')}
                />
            ))}
        </div>
    );
}


function ErrorBanner({ message, className = '' }: { message: string; className?: string }) {
    return (
        <div className={['flex items-start gap-2 rounded-lg border border-red-200 bg-red-50/70 px-3 py-2 text-xs text-red-800', className].join(' ')}>
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>{message}</span>
        </div>
    );
}


// Re-export the icons used inline so the parent page can show
// matching iconography in its banner. Optional helper.
export const ChecklistIcons = { MicOff, VideoOff };
export default PreMeetingChecklist;
